// src/components/JarvislyComponents/EnrichedEntityCard/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useRef } from 'react';
import { Flex, Typography, Divider, Descriptions, Collapse, Tag, Grid, theme, Alert } from 'antd';
import {
  ShopOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  BankOutlined,
  WhatsAppOutlined,
  InfoCircleOutlined,
  AuditOutlined,
  TeamOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import translateX from 'utils/translateX';
import { toCapitalCase } from 'utils/helpers';
import { CRT_TAG_CONFIG } from 'services/fiscalService';
import useZustand from 'hooks/useZustand';
import showEnrichedEntityModal from '../EnrichedEntityModal';

const { Text } = Typography;
const GOOGLE_MAP_LIBRARIES = ['marker'];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * EnrichedEntityCard - Reusable component for displaying enriched entity data
 *
 * @param {Object} props
 * @param {Object} props.entity - The entity data (normalized cnpjData)
 * @param {Object} props.enrichment - Enrichment data (cep, geolocation, whatsapp)
 * @param {Object} props.meta - Meta info (engine, creditCost, etc.)
 * @param {boolean} props.showHeader - Show entity header with icon and name (default: true)
 * @param {boolean} props.showNameInBasicData - Show name/fullName in basic data section (default: false)
 * @param {boolean} props.showFooter - Show footer with meta info (default: true)
 * @param {boolean} props.showActions - Show action buttons in footer (default: false)
 * @param {Object} props.visibleSections - Control which sections are visible
 * @param {ReactNode} props.footerActions - Custom footer actions (buttons, links)
 * @param {string} props.className - Additional CSS class
 * @param {string} props.dataCreatedAt - Date when the enrichment data was created (for freshness alert)
 * @param {string} props.entityId - Entity ID (for refresh functionality)
 * @param {Function} props.onRefresh - Callback after successful refresh
 */
const EnrichedEntityCard = ({
  entity,
  enrichment = {},
  meta = {},
  showHeader = true,
  showNameInBasicData = false,
  showFooter = true,
  showActions = false,
  visibleSections = {},
  footerActions = null,
  className = '',
  dataCreatedAt = null,
  entityId = null,
  onRefresh = null,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const storedModuleConfig = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const screens = Grid.useBreakpoint();
  const markerRef = useRef(null);
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_APIKEY,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !screens.md;
  const isCompany = entity?.entityType === 'company';
  // Check if we're in the dataSearch module - only hide the alert there
  const isDataSearchModule = storedModuleConfig?.id === 'dataSearch';

  // Calculate data age for freshness alert
  const dataAgeInfo = calculateDataAge(dataCreatedAt);

  // Default all sections to visible if not specified
  const sections = {
    basicData: true,
    contact: true,
    address: true,
    activities: true,
    fiscalData: true,
    partners: true,
    ...visibleSections,
  };

  // Get primary document (CNPJ or CPF)
  const cnpjDoc = entity?.documents?.find(d => d.documentType === 'cnpj');
  const cpfDoc = entity?.documents?.find(d => d.documentType === 'cpf');

  // Document value and type for refresh modal
  const documentValue = cnpjDoc?.value || cpfDoc?.value || entity?.cnpj || entity?.cpf || '';
  const documentType = cnpjDoc?.value || entity?.cnpj ? 'cnpj' : 'cpf';

  const geoEnrichment = enrichment.geolocation?.success ? enrichment.geolocation.data : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  function handleRefreshClick() {
    showEnrichedEntityModal({
      entityId,
      documentValue,
      documentType,
      currentEngine: meta.engine || 'hod',
      onSuccess: result => {
        if (onRefresh) {
          onRefresh(result);
        }
      },
    });
  }

  // Get contacts by type
  const phones = entity?.contacts?.filter(c => c.contactType === 'phone') || [];
  const emails = entity?.contacts?.filter(c => c.contactType === 'email') || [];
  const faxes = entity?.contacts?.filter(c => c.contactType === 'fax') || [];

  // Get primary address
  const primaryAddress = entity?.addresses?.find(a => a.isPrimary) || entity?.addresses?.[0];

  // Get activities
  const mainActivity = entity?.activities?.find(a => a.isPrimary) || entity?.mainActivity;
  const secondaryActivities =
    entity?.activities?.filter(a => !a.isPrimary) || entity?.additionalActivities || [];

  // Get registration status
  const registrationStatus = entity?.registrationStatus;
  const isActive =
    registrationStatus?.status === 'active' ||
    registrationStatus?.statusRaw === 'active' ||
    registrationStatus?.statusCode === 2;

  // Get fiscal data
  const fiscalData = entity?.fiscalData;

  // Get state registrations
  const stateRegistrations = entity?.stateRegistrations || [];

  // Get holders
  const holders = entity?.holders || [];

  // Get establishment type
  const establishmentType = entity?.establishmentType;
  const establishmentTypeRaw = entity?.establishmentTypeRaw;
  const isHeadquarters =
    establishmentType === 'headquarters' || establishmentTypeRaw === 'headquarters';

  // Get company size
  const companySize = entity?.size || entity?.companySize;
  const companySizeCode = entity?.sizeCode || entity?.companySizeCode;
  const companySizeRaw = entity?.companySizeRaw;

  // Get update date
  const updatedAt = entity?.updatedAt;

  // Get enrichment data
  const cepEnrichment = enrichment.cep?.success ? enrichment.cep.data : null;
  const whatsappEnrichment = enrichment.whatsapp?.success ? enrichment.whatsapp.data : null;

  // Build WhatsApp lookup map
  const whatsappMap = buildWhatsappMap(whatsappEnrichment);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className={className} style={{ padding: showHeader ? 0 : 0 }}>
      {/* Entity Header: Icon + Name/Type */}
      {showHeader && (
        <Flex gap={16} align="flex-start" style={{ marginBottom: 20 }}>
          {/* Entity type icon */}
          <Flex
            align="center"
            justify="center"
            style={{
              width: 64,
              height: 64,
              borderRadius: 8,
              backgroundColor: token.colorPrimaryBg,
              flexShrink: 0,
            }}
          >
            {isCompany ? (
              <ShopOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            ) : (
              <UserOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            )}
          </Flex>

          {/* Name and type */}
          <Flex vertical justify="center" style={{ minHeight: 64 }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {toCapitalCase(entity?.name || entity?.fullName || '')}
            </div>
            {entity?.name && entity?.fullName && entity?.name !== entity?.fullName && (
              <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
                {toCapitalCase(entity.fullName)}
              </Text>
            )}
          </Flex>
        </Flex>
      )}

      {/* Basic Data Section */}
      {sections.basicData && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          activeKey={['basic-data']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'basic-data',
              label: (
                <Flex align="center" gap={8}>
                  <InfoCircleOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('basic_data')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={isMobile ? 1 : 2}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                >
                  {/* Name fields - only when showNameInBasicData is true */}
                  {showNameInBasicData && entity?.fullName && (
                    <Descriptions.Item label={translateX('legal_name')}>
                      {toCapitalCase(entity.fullName)}
                    </Descriptions.Item>
                  )}
                  {showNameInBasicData && entity?.name && (
                    <Descriptions.Item label={translateX('trade_name')}>
                      {toCapitalCase(entity.name)}
                    </Descriptions.Item>
                  )}

                  {/* CNPJ with tag */}
                  {(cnpjDoc?.value || entity?.cnpj) && (
                    <Descriptions.Item label={translateX('cnpj')}>
                      <Flex align="center" gap={8}>
                        {formatCnpj(cnpjDoc?.value || entity?.cnpj)}
                        {(establishmentType || establishmentTypeRaw) && (
                          <Tag
                            color={isHeadquarters ? 'success' : 'processing'}
                            style={{ marginLeft: 4 }}
                          >
                            {isHeadquarters
                              ? translateX('establishment_headquarters') || 'Matriz'
                              : translateX('establishment_branch') || 'Filial'}
                          </Tag>
                        )}
                      </Flex>
                    </Descriptions.Item>
                  )}

                  {/* CPF (when no CNPJ) */}
                  {cpfDoc?.value && !cnpjDoc?.value && !entity?.cnpj && (
                    <Descriptions.Item label={translateX('cpf')}>
                      {formatCpf(cpfDoc.value)}
                    </Descriptions.Item>
                  )}

                  {/* Status */}
                  <Descriptions.Item
                    label={translateX('status_short')}
                    span={!cnpjDoc?.value && !entity?.cnpj && !cpfDoc?.value && !isMobile ? 2 : 1}
                  >
                    {registrationStatus ? (
                      <Flex vertical gap={2}>
                        <Flex align="center" gap={6}>
                          {isActive ? (
                            <CheckCircleOutlined style={{ color: 'var(--ant-color-success)' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: 'var(--ant-color-error)' }} />
                          )}
                          <span>
                            {registrationStatus.statusRaw === 'active'
                              ? translateX('active') || 'Ativa'
                              : registrationStatus.statusRaw || '-'}
                          </span>
                        </Flex>
                        {registrationStatus.date && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {translateX('in_date')}:{' '}
                            {new Date(registrationStatus.date).toLocaleDateString('pt-BR')}
                          </Text>
                        )}
                      </Flex>
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>

                  {/* Legal Nature (span 2 on desktop) */}
                  <Descriptions.Item label={translateX('nature_short')} span={isMobile ? 1 : 2}>
                    {formatLegalNature(entity?.legalNature, entity?.legalNatureCode)}
                  </Descriptions.Item>

                  {/* Size | Capital */}
                  <Descriptions.Item label={translateX('size_short')}>
                    {companySize || companySizeCode || companySizeRaw || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translateX('capital_short')}>
                    {entity?.shareCapital !== undefined && entity?.shareCapital !== null
                      ? formatPrice(entity.shareCapital)
                      : '-'}
                  </Descriptions.Item>

                  {/* Foundation | Update */}
                  <Descriptions.Item label={translateX('foundation_short')}>
                    {entity?.foundedAt
                      ? new Date(entity.foundedAt).toLocaleDateString('pt-BR')
                      : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={translateX('update_short')}>
                    {updatedAt ? new Date(updatedAt).toLocaleDateString('pt-BR') : '-'}
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Contact Channels Section */}
      {sections.contact && (phones.length > 0 || emails.length > 0 || faxes.length > 0) && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          activeKey={['contact-channels']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'contact-channels',
              label: (
                <Flex align="center" gap={8}>
                  <PhoneOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('contact_channels')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                >
                  {/* Phones */}
                  {(phones.length > 0 || faxes.length > 0) && (
                    <Descriptions.Item label={translateX('phones')}>
                      <Flex vertical gap={4}>
                        {[...phones, ...faxes].map((phone, idx) => (
                          <Flex key={idx} align="center">
                            {formatPhone(phone.value)}
                            {renderWhatsappStatus(phone.value, whatsappMap, token)}
                          </Flex>
                        ))}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {/* Emails */}
                  {emails.length > 0 && (
                    <Descriptions.Item label={translateX('emails')}>
                      <Flex vertical gap={4}>
                        {emails.map((email, idx) => (
                          <div key={idx}>{email.value}</div>
                        ))}
                      </Flex>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Address Section */}
      {sections.address && primaryAddress && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          activeKey={['address']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'address',
              label: (
                <Flex align="center" gap={8}>
                  <EnvironmentOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('address')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <>
                  <Descriptions
                    bordered
                    size="small"
                    column={1}
                    layout={isMobile ? 'vertical' : 'horizontal'}
                  >
                    {/* Full address stacked */}
                    <Descriptions.Item label={translateX('address')}>
                      <Flex vertical gap={2}>
                        {(primaryAddress.address1 || primaryAddress.number) && (
                          <Text>
                            {[primaryAddress.address1, primaryAddress.number]
                              .filter(Boolean)
                              .join(', ')}
                          </Text>
                        )}
                        {primaryAddress.address2 && <Text>{primaryAddress.address2}</Text>}
                        {primaryAddress.neighborhood && <Text>{primaryAddress.neighborhood}</Text>}
                        {(primaryAddress.city || primaryAddress.region) && (
                          <Text>
                            {[primaryAddress.city, primaryAddress.region]
                              .filter(Boolean)
                              .join(' - ')}
                          </Text>
                        )}
                        {primaryAddress.zip && <Text>{formatCep(primaryAddress.zip)}</Text>}
                      </Flex>
                    </Descriptions.Item>

                    {/* IBGE Code */}
                    {(cepEnrichment?.ibgeCode || primaryAddress?.cityCode) && (
                      <Descriptions.Item label={translateX('ibge_code')}>
                        {cepEnrichment?.ibgeCode || primaryAddress?.cityCode}
                      </Descriptions.Item>
                    )}

                    {/* Area Code (DDD) */}
                    {cepEnrichment?.areaCode && (
                      <Descriptions.Item label={translateX('area_code')}>
                        {cepEnrichment.areaCode}
                      </Descriptions.Item>
                    )}

                    {/* GPS Coordinates */}
                    {geoEnrichment?.latitude && geoEnrichment?.longitude && (
                      <Descriptions.Item label={translateX('coordinates')}>
                        {geoEnrichment.latitude.toFixed(6)}, {geoEnrichment.longitude.toFixed(6)}
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {geoEnrichment?.latitude != null &&
                    geoEnrichment?.longitude != null &&
                    isMapLoaded && (
                      <div
                        style={{
                          width: '100%',
                          height: 200,
                          borderRadius: '0 0 8px 8px',
                          overflow: 'hidden',
                        }}
                      >
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={{
                            lat: Number(geoEnrichment.latitude),
                            lng: Number(geoEnrichment.longitude),
                          }}
                          zoom={16}
                          options={{
                            mapId: import.meta.env.VITE_GOOGLE_API_MAP_ID,
                            disableDefaultUI: false,
                            zoomControl: true,
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                          }}
                          onLoad={mapInstance => {
                            if (markerRef.current) {
                              markerRef.current.map = null;
                            }
                            const { AdvancedMarkerElement } = window.google.maps.marker;
                            markerRef.current = new AdvancedMarkerElement({
                              map: mapInstance,
                              position: {
                                lat: Number(geoEnrichment.latitude),
                                lng: Number(geoEnrichment.longitude),
                              },
                            });
                          }}
                          onUnmount={() => {
                            if (markerRef.current) {
                              markerRef.current.map = null;
                              markerRef.current = null;
                            }
                          }}
                        />
                      </div>
                    )}
                </>
              ),
            },
          ]}
        />
      )}

      {/* Economic Activities Section */}
      {sections.activities && mainActivity && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          activeKey={['economic-activities']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'economic-activities',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <BankOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('economic_activities')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  <Descriptions.Item label={translateX('main_activity')}>
                    <Text>
                      {mainActivity.code} - {toCapitalCase(mainActivity.description)}
                    </Text>
                  </Descriptions.Item>
                  {secondaryActivities?.length > 0 && (
                    <Descriptions.Item label={translateX('secondary_activities')}>
                      <Flex vertical gap={2}>
                        {secondaryActivities.map((activity, index) => (
                          <Text key={index} type="secondary" style={{ fontSize: 12 }}>
                            {activity.code} - {toCapitalCase(activity.description)}
                          </Text>
                        ))}
                      </Flex>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Fiscal Data Section */}
      {sections.fiscalData && fiscalData && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          activeKey={['fiscal-data']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'fiscal-data',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <AuditOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('fiscal_data')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {/* CNAE */}
                  {fiscalData.mainCnae && (
                    <Descriptions.Item label={translateX('cnae')}>
                      {fiscalData.mainCnae}
                    </Descriptions.Item>
                  )}
                  {/* Simples Nacional with CRT Tag */}
                  {fiscalData.isSimples !== undefined && (
                    <Descriptions.Item label="Simples Nacional">
                      <Flex align="center" gap={6}>
                        {fiscalData.isSimples ? (
                          <CheckCircleOutlined style={{ color: 'var(--ant-color-success)' }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: 'var(--ant-color-error)' }} />
                        )}
                        {fiscalData.isSimples ? translateX('yes') : translateX('no')}
                        {fiscalData.taxRegime && CRT_TAG_CONFIG[fiscalData.taxRegime] && (
                          <Tag
                            color={CRT_TAG_CONFIG[fiscalData.taxRegime].color}
                            style={{ marginLeft: 4 }}
                          >
                            {CRT_TAG_CONFIG[fiscalData.taxRegime].label}
                          </Tag>
                        )}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {/* Simples entry date */}
                  {fiscalData.simplesOptedAt && (
                    <Descriptions.Item label={translateX('simples_entry')}>
                      {new Date(fiscalData.simplesOptedAt).toLocaleDateString('pt-BR')}
                    </Descriptions.Item>
                  )}
                  {/* Simples exclusion date */}
                  {fiscalData.simplesExcludedAt && (
                    <Descriptions.Item label={translateX('simples_exclusion')}>
                      {new Date(fiscalData.simplesExcludedAt).toLocaleDateString('pt-BR')}
                    </Descriptions.Item>
                  )}
                  {/* MEI */}
                  {fiscalData.isMei !== undefined && (
                    <Descriptions.Item label="MEI">
                      <Flex align="center" gap={6}>
                        {fiscalData.isMei ? (
                          <CheckCircleOutlined style={{ color: 'var(--ant-color-success)' }} />
                        ) : (
                          <CloseCircleOutlined style={{ color: 'var(--ant-color-error)' }} />
                        )}
                        {fiscalData.isMei ? translateX('yes') : translateX('no')}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {/* Simples/MEI data update */}
                  {fiscalData.simplesUpdatedAt && (
                    <Descriptions.Item label={translateX('simples_updated_at')}>
                      {new Date(fiscalData.simplesUpdatedAt).toLocaleDateString('pt-BR')}
                    </Descriptions.Item>
                  )}
                  {/* State Registrations */}
                  <Descriptions.Item label={translateX('state_registrations')}>
                    {stateRegistrations?.length > 0 ? (
                      <Flex vertical gap={8}>
                        {stateRegistrations.map((ie, index) => (
                          <Flex key={index} vertical gap={2}>
                            <Flex align="center" gap={6}>
                              {ie.isActive !== undefined &&
                                (ie.isActive ? (
                                  <CheckCircleOutlined
                                    style={{ color: 'var(--ant-color-success)', fontSize: 14 }}
                                  />
                                ) : (
                                  <CloseCircleOutlined
                                    style={{ color: 'var(--ant-color-error)', fontSize: 14 }}
                                  />
                                ))}
                              <Text>
                                {ie.state || ie.stateName} - {ie.stateRegistration}
                              </Text>
                            </Flex>
                            {ie.updatedAt && (
                              <Text type="secondary" style={{ fontSize: 11, marginLeft: 20 }}>
                                {translateX('updated')}:{' '}
                                {new Date(ie.updatedAt).toLocaleDateString('pt-BR')}
                              </Text>
                            )}
                          </Flex>
                        ))}
                      </Flex>
                    ) : (
                      <Text>{translateX('exempt')}</Text>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Partners/Holders Section */}
      {sections.partners && holders?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          activeKey={['partners']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'partners',
              showArrow: false,
              label: (
                <Flex align="center" gap={8}>
                  <TeamOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('partners')}</span>
                </Flex>
              ),
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  <Descriptions.Item label={translateX('partners')}>
                    <Flex vertical gap={16}>
                      {holders.slice(0, 10).map((holder, index) => (
                        <Flex key={index} vertical gap={2}>
                          {/* Name */}
                          <Text strong>{toCapitalCase(holder.name)}</Text>
                          {/* CPF (masked) */}
                          {holder.cpfMasked && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              CPF: {holder.cpfMasked}
                            </Text>
                          )}
                          {/* Entry date */}
                          {holder.sinceAt && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {translateX('since')}:{' '}
                              {new Date(holder.sinceAt).toLocaleDateString('pt-BR')}
                            </Text>
                          )}
                          {/* Age range */}
                          {(holder.ageRange || holder.ageRangeRaw) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {translateX('age_range')}:{' '}
                              {holder.ageRange
                                ? translateX(`age_range_${holder.ageRange}`)
                                : holder.ageRangeRaw}
                            </Text>
                          )}
                        </Flex>
                      ))}
                      {holders.length > 10 && (
                        <Text type="secondary">
                          +{holders.length - 10} {translateX('more_partners')}
                        </Text>
                      )}
                    </Flex>
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* Data Freshness Alert - Only show when not in dataSearch module and has dataCreatedAt */}
      {showFooter && !isDataSearchModule && dataAgeInfo && (
        <Alert
          type={dataAgeInfo.alertType}
          showIcon
          icon={<SyncOutlined />}
          style={{ marginTop: 16, cursor: 'pointer' }}
          title={
            <span style={{ cursor: 'pointer' }}>
              {translateX('data_queried_x_days_ago', { days: dataAgeInfo.days })}
              {' '}
              <Text
                underline
                style={{ color: token.colorPrimary, cursor: 'pointer' }}
              >
                {translateX('click_to_refresh')}
              </Text>
            </span>
          }
          onClick={e => {
            e.stopPropagation();
            handleRefreshClick();
          }}
        />
      )}

      {/* Footer with meta info */}
      {showFooter && (
        <div style={{ marginTop: 16 }}>
          <Divider style={{ margin: '12px 0' }} />
          <Flex
            vertical={isMobile}
            justify={isMobile ? 'center' : 'space-between'}
            align="center"
            wrap="wrap"
            gap={isMobile ? 12 : 16}
          >
            {/* Meta stats */}
            <Flex gap={isMobile ? 16 : 24} wrap="wrap" justify={isMobile ? 'center' : 'flex-start'}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{translateX('engine')}:</span>{' '}
                {meta.engine?.toUpperCase() || 'HOD'}
              </Text>
              {meta.creditCost !== undefined && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 500 }}>{translateX('credits_debited')}:</span>{' '}
                  {meta.creditCost}
                </Text>
              )}
            </Flex>

            {/* Action buttons (optional) */}
            {showActions && footerActions && (
              <Flex gap={16} align="center" justify={isMobile ? 'center' : 'flex-end'}>
                {footerActions}
              </Flex>
            )}
          </Flex>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EnrichedEntityCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Calculate data age and determine alert type
 * @param {string} createdAt - ISO date string when data was created
 * @returns {Object|null} { days, months, alertType } or null if no date
 */
function calculateDataAge(createdAt) {
  if (!createdAt) return null;

  const createdDate = new Date(createdAt);
  if (isNaN(createdDate.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - createdDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = diffDays / 30;

  let alertType = 'info';
  if (diffMonths >= 12) {
    alertType = 'error';
  } else if (diffMonths >= 6) {
    alertType = 'warning';
  }

  return {
    days: diffDays,
    months: Math.floor(diffMonths),
    alertType,
  };
}

/**
 * Build WhatsApp lookup map for phones (phone number -> hasWhatsapp)
 */
function buildWhatsappMap(whatsappEnrichment) {
  const whatsappMap = {};
  if (whatsappEnrichment?.numbers) {
    whatsappEnrichment.numbers.forEach(n => {
      const cleanPhone = n.phoneNumber?.replace(/\D/g, '');
      if (cleanPhone) {
        whatsappMap[cleanPhone] = n.hasWhatsapp;
        if (cleanPhone.startsWith('55')) {
          whatsappMap[cleanPhone.substring(2)] = n.hasWhatsapp;
        }
      }
    });
  }
  return whatsappMap;
}

/**
 * Render WhatsApp status icon for a phone number
 */
function renderWhatsappStatus(phoneValue, whatsappMap, token) {
  const cleanPhone = phoneValue?.replace(/\D/g, '');
  if (!cleanPhone || Object.keys(whatsappMap).length === 0) return null;

  const hasWhatsapp = whatsappMap[cleanPhone] ?? whatsappMap[`55${cleanPhone}`];

  if (hasWhatsapp === true) {
    return (
      <Tooltip title={translateX('has_whatsapp')}>
        <WhatsAppOutlined style={{ color: '#25D366', marginLeft: 8 }} />
      </Tooltip>
    );
  } else if (hasWhatsapp === false) {
    return (
      <Tooltip title={translateX('no_whatsapp')}>
        <WhatsAppOutlined style={{ color: token.colorTextDisabled, marginLeft: 8 }} />
      </Tooltip>
    );
  }
  return null;
}

/**
 * Format legal nature with code
 */
function formatLegalNature(legalNature, legalNatureCode) {
  if (!legalNature && !legalNatureCode) return '-';

  if (typeof legalNature === 'string') {
    if (legalNatureCode) return `${legalNatureCode} - ${legalNature}`;
    return legalNature;
  }

  if (typeof legalNature === 'object' && legalNature !== null) {
    const code = legalNature.code || legalNatureCode;
    const description = legalNature.description;
    if (code && description) return `${code} - ${description}`;
    if (description) return description;
    if (code) return code;
  }

  if (legalNatureCode) return legalNatureCode;

  return '-';
}

/**
 * Format CNPJ to display format
 */
function formatCnpj(value) {
  if (!value) return '';
  const strValue = typeof value === 'string' ? value : String(value);
  const cnpj = strValue.replace(/\D/g, '');
  if (cnpj.length !== 14) return strValue;
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Format CPF to display format
 */
function formatCpf(value) {
  if (!value) return '';
  const strValue = typeof value === 'string' ? value : String(value);
  const cpf = strValue.replace(/\D/g, '');
  if (cpf.length !== 11) return strValue;
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Format phone number
 */
function formatPhone(value) {
  if (!value) return '';
  const phone = (typeof value === 'string' ? value : String(value)).replace(/\D/g, '');
  if (phone.length === 11) {
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (phone.length === 10) {
    return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return value;
}

/**
 * Format CEP to display format (XXXXX-XXX)
 */
function formatCep(value) {
  if (!value) return '';
  const strValue = typeof value === 'string' ? value : String(value);
  const cep = strValue.replace(/\D/g, '');
  if (cep.length !== 8) return strValue;
  return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Format price to BRL currency
 */
function formatPrice(value) {
  if (value === null || value === undefined || isNaN(value)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}
